import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    useTheme,
    Button,
    Skeleton,
    Divider,
    LinearProgress,
    useMediaQuery
} from '@mui/material';
import {
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    EventNote as CalendarIcon,
    AttachMoney as MoneyIcon,
    TrendingUp as ProfitIcon,
    ShoppingCart as SoldIcon,
    Today as TodayIcon,
    Receipt as ReceiptIcon
} from '@mui/icons-material';
import { inventoryService } from '../services/InventoryService';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const SalesCalendar = ({ shopName }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [salesData, setSalesData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Helper to standard currency format
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-PH', {
            style: 'currency',
            currency: 'PHP',
            minimumFractionDigits: 2
        }).format(amount);
    };

    const isSameDay = (d1, d2) => {
        return d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate();
    };

    // Fetch sales for the current month
    useEffect(() => {
        const fetchMonthSales = async () => {
            setLoading(true);
            try {
                const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59, 999);
                const sales = await inventoryService.getSalesActivities(shopName, start, end);
                setSalesData(sales);
            } catch (error) {
                console.error('Error fetching sales:', error);
                setSalesData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchMonthSales();
    }, [currentDate, shopName]);

    // Month stats
    const monthStats = useMemo(() => {
        return salesData.reduce((acc, sale) => {
            const revenue = sale.saleAmount || 0;
            const cost = (sale.itemCost || 0) * (sale.quantitySold || 1);
            const profit = revenue - cost;
            return {
                totalRevenue: acc.totalRevenue + revenue,
                totalProfit: acc.totalProfit + profit,
                totalItemsSold: acc.totalItemsSold + (sale.quantitySold || 0),
                totalTransactions: acc.totalTransactions + 1
            };
        }, { totalRevenue: 0, totalProfit: 0, totalItemsSold: 0, totalTransactions: 0 });
    }, [salesData]);

    // Daily sales for selected date
    const dailySales = useMemo(() => {
        const startOfDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
        const endOfDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59, 999);
        return salesData.filter(sale => {
            const saleDate = new Date(sale.timestamp);
            return saleDate >= startOfDay && saleDate <= endOfDay;
        });
    }, [selectedDate, salesData]);

    // Daily stats for selected date
    const dailyStats = useMemo(() => {
        return dailySales.reduce((acc, sale) => {
            const revenue = sale.saleAmount || 0;
            const cost = (sale.itemCost || 0) * (sale.quantitySold || 1);
            const profit = revenue - cost;
            return {
                totalRevenue: acc.totalRevenue + revenue,
                totalProfit: acc.totalProfit + profit,
                totalItemsSold: acc.totalItemsSold + (sale.quantitySold || 0),
            };
        }, { totalRevenue: 0, totalProfit: 0, totalItemsSold: 0 });
    }, [dailySales]);

    // Build map of day -> sales count and revenue for calendar badges
    const dayStatsMap = useMemo(() => {
        const map = {};
        salesData.forEach(sale => {
            const d = new Date(sale.timestamp);
            const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            if (!map[key]) {
                map[key] = { count: 0, revenue: 0, profit: 0 };
            }
            map[key].count += (sale.quantitySold || 0);
            map[key].revenue += (sale.saleAmount || 0);
            const cost = (sale.itemCost || 0) * (sale.quantitySold || 1);
            map[key].profit += ((sale.saleAmount || 0) - cost);
        });
        return map;
    }, [salesData]);

    const handlePrevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const handleToday = () => {
        const now = new Date();
        setCurrentDate(now);
        setSelectedDate(now);
    };

    // Generate calendar days array
    const calendarDays = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        const startDate = new Date(firstDay);
        startDate.setDate(1 - firstDay.getDay());

        const endDate = new Date(lastDay);
        if (lastDay.getDay() < 6) {
            endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));
        }

        const days = [];
        const day = new Date(startDate);
        while (day <= endDate) {
            days.push(new Date(day));
            day.setDate(day.getDate() + 1);
        }
        return days;
    }, [currentDate]);

    const monthYearLabel = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 3 } }}>
            {/* ───────────── HEADER ───────────── */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 44, height: 44, borderRadius: 2,
                        background: '#E53935',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <CalendarIcon sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            Sales Calendar
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Track daily &amp; monthly performance
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* ───────────── MONTH STATS CARDS ───────────── */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                gap: 2, mb: 3
            }}>
                {[
                    {
                        label: 'Total Revenue',
                        value: formatCurrency(monthStats.totalRevenue),
                        icon: <MoneyIcon />,
                        color: '#E53935',
                        bg: 'rgba(229,57,53,0.08)',
                        border: 'rgba(229,57,53,0.15)'
                    },
                    {
                        label: 'Net Profit',
                        value: formatCurrency(monthStats.totalProfit),
                        icon: <ProfitIcon />,
                        color: monthStats.totalProfit >= 0 ? '#059669' : '#dc2626',
                        bg: monthStats.totalProfit >= 0
                            ? 'linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(5,150,105,0.05) 100%)'
                            : 'linear-gradient(135deg, rgba(220,38,38,0.08) 0%, rgba(185,28,28,0.05) 100%)',
                        border: monthStats.totalProfit >= 0 ? 'rgba(16,185,129,0.15)' : 'rgba(220,38,38,0.15)'
                    },
                    {
                        label: 'Items Sold',
                        value: monthStats.totalItemsSold,
                        icon: <SoldIcon />,
                        color: '#7c3aed',
                        bg: 'linear-gradient(135deg, rgba(124,58,237,0.08) 0%, rgba(109,40,217,0.05) 100%)',
                        border: 'rgba(124,58,237,0.15)'
                    },
                    {
                        label: 'Transactions',
                        value: monthStats.totalTransactions,
                        icon: <ReceiptIcon />,
                        color: '#ea580c',
                        bg: 'linear-gradient(135deg, rgba(234,88,12,0.08) 0%, rgba(194,65,12,0.05) 100%)',
                        border: 'rgba(234,88,12,0.15)'
                    }
                ].map((stat) => (
                    <Card key={stat.label} elevation={0} sx={{
                        background: stat.bg,
                        border: `1px solid ${stat.border}`,
                        borderRadius: 3,
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 25px rgba(0,0,0,0.3)' }
                    }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box sx={{
                                    width: 32, height: 32, borderRadius: 1.5,
                                    bgcolor: `${stat.color}15`, color: stat.color,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    '& svg': { fontSize: 18 }
                                }}>
                                    {stat.icon}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}>
                                    {stat.label}
                                </Typography>
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: stat.color, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                                {loading ? <Skeleton width={80} /> : stat.value}
                            </Typography>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            {/* ───────────── MAIN LAYOUT: CALENDAR + DETAILS ───────────── */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: '1fr 380px' },
                gap: 3
            }}>
                {/* ───── CALENDAR ───── */}
                <Paper elevation={0} sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.08)',
                    background: '#2D2D2D',
                    overflow: 'hidden'
                }}>
                    {/* Calendar Header */}
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        p: { xs: 1.5, sm: 2 },
                        borderBottom: '1px solid rgba(255,255,255,0.08)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconButton onClick={handlePrevMonth} size="small" sx={{
                                bgcolor: 'rgba(255,255,255,0.04)', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                            }}>
                                <ChevronLeftIcon />
                            </IconButton>
                            <Typography variant="h6" sx={{
                                fontWeight: 700, minWidth: 200, textAlign: 'center',
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                {monthYearLabel}
                            </Typography>
                            <IconButton onClick={handleNextMonth} size="small" sx={{
                                bgcolor: 'rgba(255,255,255,0.04)', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                            }}>
                                <ChevronRightIcon />
                            </IconButton>
                        </Box>
                        <Button
                            startIcon={<TodayIcon />}
                            size="small"
                            variant="outlined"
                            onClick={handleToday}
                            sx={{
                                borderColor: 'rgba(229,57,53,0.3)', color: '#E53935',
                                fontWeight: 600, fontSize: '0.8rem',
                                '&:hover': { borderColor: '#E53935', bgcolor: 'rgba(229,57,53,0.04)' }
                            }}
                        >
                            Today
                        </Button>
                    </Box>

                    {loading && <LinearProgress sx={{ height: 2 }} />}

                    {/* Day-of-week headers */}
                    <Box sx={{
                        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                        bgcolor: 'rgba(255,255,255,0.02)'
                    }}>
                        {DAYS_OF_WEEK.map(d => (
                            <Typography key={d} sx={{
                                py: 1, textAlign: 'center', fontWeight: 700,
                                color: (d === 'Sun' || d === 'Sat') ? 'error.light' : 'text.secondary',
                                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                textTransform: 'uppercase', letterSpacing: '0.05em'
                            }}>
                                {d}
                            </Typography>
                        ))}
                    </Box>

                    {/* Calendar Grid */}
                    <Box sx={{
                        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                    }}>
                        {calendarDays.map((dayDate, idx) => {
                            const key = `${dayDate.getFullYear()}-${dayDate.getMonth()}-${dayDate.getDate()}`;
                            const stats = dayStatsMap[key];
                            const isCurrentMonth = dayDate.getMonth() === currentDate.getMonth();
                            const isSelected = isSameDay(dayDate, selectedDate);
                            const isToday = isSameDay(dayDate, new Date());
                            const dayOfWeek = dayDate.getDay();

                            return (
                                <Box
                                    key={idx}
                                    onClick={() => {
                                        setSelectedDate(new Date(dayDate));
                                        // If clicking a day from a different month, navigate to that month
                                        if (dayDate.getMonth() !== currentDate.getMonth()) {
                                            setCurrentDate(new Date(dayDate.getFullYear(), dayDate.getMonth(), 1));
                                        }
                                    }}
                                    sx={{
                                        minHeight: { xs: 64, sm: 80, md: 90 },
                                        p: { xs: 0.5, sm: 0.75 },
                                        cursor: 'pointer',
                                        borderRight: (idx % 7 !== 6) ? '1px solid rgba(255,255,255,0.04)' : 'none',
                                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                                        bgcolor: isSelected
                                            ? 'rgba(229,57,53,0.08)'
                                            : isCurrentMonth
                                                ? 'transparent'
                                                : 'rgba(0,0,0,0.15)',
                                        opacity: isCurrentMonth ? 1 : 0.45,
                                        transition: 'all 0.15s ease',
                                        position: 'relative',
                                        '&:hover': {
                                            bgcolor: isSelected ? 'rgba(229,57,53,0.12)' : 'rgba(255,255,255,0.03)',
                                        },
                                        ...(isSelected && {
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                height: 3,
                                                background: '#E53935',
                                                borderRadius: '0 0 2px 2px'
                                            }
                                        })
                                    }}
                                >
                                    {/* Day number */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                        <Box sx={{
                                            width: { xs: 22, sm: 28 }, height: { xs: 22, sm: 28 },
                                            borderRadius: '50%',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            bgcolor: isToday ? '#E53935' : 'transparent',
                                            color: isToday ? 'white' : (dayOfWeek === 0 || dayOfWeek === 6) ? 'error.light' : 'text.primary',
                                        }}>
                                            <Typography sx={{
                                                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                                fontWeight: isToday || isSelected ? 700 : 500,
                                            }}>
                                                {dayDate.getDate()}
                                            </Typography>
                                        </Box>
                                        {stats && stats.count > 0 && (
                                            <Chip
                                                label={stats.count}
                                                size="small"
                                                sx={{
                                                    height: 18, fontSize: '0.6rem', fontWeight: 700,
                                                    bgcolor: 'rgba(229,57,53,0.1)', color: '#E53935',
                                                    '& .MuiChip-label': { px: 0.75 }
                                                }}
                                            />
                                        )}
                                    </Box>

                                    {/* Revenue & Profit */}
                                    {stats && stats.revenue > 0 && !isMobile && (
                                        <Box sx={{ mt: 0.25 }}>
                                            <Typography sx={{
                                                fontSize: '0.6rem', fontWeight: 700,
                                                color: '#059669', lineHeight: 1.4, whiteSpace: 'nowrap',
                                                overflow: 'hidden', textOverflow: 'ellipsis'
                                            }}>
                                                {formatCurrency(stats.revenue)}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: '0.55rem', fontWeight: 600,
                                                color: stats.profit >= 0 ? 'rgba(5,150,105,0.7)' : 'error.main',
                                                lineHeight: 1.4
                                            }}>
                                                Net: {formatCurrency(stats.profit)}
                                            </Typography>
                                        </Box>
                                    )}
                                    {stats && stats.revenue > 0 && isMobile && (
                                        <Box sx={{
                                            width: 6, height: 6, borderRadius: '50%',
                                            bgcolor: '#059669', mx: 'auto', mt: 0.5
                                        }} />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Paper>

                {/* ───── DAILY DETAILS SIDEBAR ───── */}
                <Paper elevation={0} sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.08)',
                    background: '#2D2D2D',
                    overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                    maxHeight: { lg: 620 }
                }}>
                    {/* Header */}
                    <Box sx={{
                        background: '#E53935',
                        color: 'white', p: { xs: 2, sm: 2.5 }
                    }}>
                        <Typography variant="subtitle2" sx={{ opacity: 0.7, fontWeight: 500, mb: 0.5, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            Daily Breakdown
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                            {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                        </Typography>

                        {/* Daily mini-stats */}
                        <Box sx={{
                            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 1.5, mt: 2
                        }}>
                            {[
                                { label: 'Revenue', value: formatCurrency(dailyStats.totalRevenue) },
                                { label: 'Net Profit', value: formatCurrency(dailyStats.totalProfit) },
                                { label: 'Items', value: dailyStats.totalItemsSold }
                            ].map(s => (
                                <Box key={s.label} sx={{
                                    bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, p: 1,
                                    backdropFilter: 'blur(10px)'
                                }}>
                                    <Typography sx={{ fontSize: '0.6rem', opacity: 0.7, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {s.label}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mt: 0.25 }}>
                                        {s.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Sales list */}
                    <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                        {dailySales.length === 0 ? (
                            <Box sx={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                justifyContent: 'center', py: 6, px: 3
                            }}>
                                <Box sx={{
                                    width: 64, height: 64, borderRadius: 3,
                                    bgcolor: 'rgba(0,0,0,0.2)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2
                                }}>
                                    <CalendarIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
                                </Box>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                    No Sales
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center' }}>
                                    No transactions recorded for this date.
                                </Typography>
                            </Box>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: '2px solid rgba(255,255,255,0.08)' }}>
                                                Item
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: '2px solid rgba(255,255,255,0.08)' }}>
                                                Amount
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: '2px solid rgba(255,255,255,0.08)' }}>
                                                Profit
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dailySales.map((sale) => {
                                            const cost = (sale.itemCost || 0) * (sale.quantitySold || 1);
                                            const profit = (sale.saleAmount || 0) - cost;
                                            const unitPrice = (sale.saleAmount || 0) / (sale.quantitySold || 1);

                                            return (
                                                <TableRow key={sale.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                                                    <TableCell sx={{ py: 1.5 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                                            {sale.itemName}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                                                {sale.quantitySold} × {formatCurrency(unitPrice)}
                                                            </Typography>
                                                            {sale.itemBrand && (
                                                                <Chip label={sale.itemBrand} size="small" sx={{
                                                                    height: 16, fontSize: '0.6rem',
                                                                    bgcolor: 'rgba(255,255,255,0.06)', fontWeight: 500,
                                                                    '& .MuiChip-label': { px: 0.75 }
                                                                }} />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ py: 1.5 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                                            {formatCurrency(sale.saleAmount || 0)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ py: 1.5 }}>
                                                        <Typography variant="body2" sx={{
                                                            fontWeight: 700, fontSize: '0.85rem',
                                                            color: profit >= 0 ? '#059669' : '#dc2626'
                                                        }}>
                                                            {profit >= 0 ? '+' : ''}{formatCurrency(profit)}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>

                    {/* Footer totals */}
                    {dailySales.length > 0 && (
                        <Box sx={{
                            p: 2, borderTop: '2px solid rgba(255,255,255,0.08)',
                            bgcolor: 'rgba(0,0,0,0.15)'
                        }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Total Revenue
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {formatCurrency(dailyStats.totalRevenue)}
                                </Typography>
                            </Box>
                            <Divider sx={{ my: 0.75 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                    Net Profit
                                </Typography>
                                <Typography variant="h6" sx={{
                                    fontWeight: 800,
                                    color: dailyStats.totalProfit >= 0 ? '#059669' : '#dc2626'
                                }}>
                                    {dailyStats.totalProfit >= 0 ? '+' : ''}{formatCurrency(dailyStats.totalProfit)}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

export default SalesCalendar;
